import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { decideReview, listLibrary, listReviews, requestShare, retireAgent } from './client';
import { keys } from './keys';

/**
 * The org library (AB-2-5, ADR-0081 §2/§3). It changes only when someone shares, reviews
 * or retires — each mutation below invalidates what it touched — so nothing polls.
 * The server re-checks ownership and roles on every call; the SPA only hides controls.
 */
export function useLibrary() {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.library(),
    queryFn: ({ signal }) => listLibrary({ getAccessToken, signal }),
    select: (response) => response.data.agents,
  });
}

/** Pending share requests — fetched only for an Owner or Admin (`enabled`). */
export function useReviews(enabled: boolean) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.reviews(),
    queryFn: ({ signal }) => listReviews({ getAccessToken, signal }),
    enabled,
    select: (response) => response.data.reviews,
  });
}

export type LibraryAction =
  { kind: 'share' } | { kind: 'retire' } | { kind: 'review'; decision: 'approve' | 'decline' };

/** Share, retire or decide on one agent; refreshes the library, the reviews, the invite
 *  list and every workstream's members (a retired agent leaves them all). */
export function useLibraryAction(agentId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.libraryAction(agentId),
    // The responses differ per action; every caller only refetches, so none is kept.
    mutationFn: async (action: LibraryAction): Promise<void> => {
      const options = { getAccessToken };
      if (action.kind === 'share') await requestShare(agentId, options);
      else if (action.kind === 'retire') await retireAgent(agentId, options);
      else await decideReview(agentId, action.decision, options);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.library() }),
        queryClient.invalidateQueries({ queryKey: keys.reviews() }),
        queryClient.invalidateQueries({ queryKey: keys.orgAgents() }),
        queryClient.invalidateQueries({ queryKey: keys.drafts() }),
        queryClient.invalidateQueries({ queryKey: keys.workstream.allMemberAgents() }),
      ]),
  });
}
